import { AxiosResponse } from 'axios';
import axiosClient from '../clients/axios-client';
import {
	ProjectConfiguration,
	ProjectConfigurationFilter,
	ProjectConfigurationResponse,
	CreateProjectConfigurationInput,
	UpdateProjectConfigurationInput,
	ProjectSubType,
} from '../data/models/projectConfiguration';
import { stringifyReqObj } from '../utils/common-utils';

export async function fetchProjectConfigurations(
	filter: ProjectConfigurationFilter
): Promise<ProjectConfigurationResponse> {
	const graphqlQuery = `{
    projectConfigurations(filter: ${stringifyReqObj(filter)}) {
      list {
        id
        created
        updated
        name
        nameId
        description
        dataCategoryLength
        foldersLength
        templatesLength
        folders {
          id
        }
        company
      }
      count
    }
  }`;
	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: 'POST',
		data: { query: graphqlQuery },
	});
	return response?.data?.projectConfigurations;
}

export async function createProjectConfiguration(
	data: CreateProjectConfigurationInput
): Promise<ProjectConfiguration> {
	const graphqlQuery = `mutation { createProjectConfiguration(data: ${stringifyReqObj(
		data
	)}) {
    id
    created
    updated
    name
    nameId
    description
    folders {
      name
      nameId
      description
      order
    }
    company
  }}`;
	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: 'POST',
		data: { query: graphqlQuery },
	});
	return response?.data?.createProjectConfiguration;
}

export async function updateProjectConfiguration(
	data: UpdateProjectConfigurationInput
): Promise<ProjectConfiguration> {
	const graphqlQuery = `mutation { updateProjectConfiguration(data: ${stringifyReqObj(
		data
	)}) {
    id
      created
      updated
      name
      nameId
      description
      folders {
            id
            display
            active
            createdBy {
                id
                name
            }
            created
            updated
            subFolders {
                id
                display
                active
                createdBy {
                    id
                    name
                }
                created
                updated
                subFolders {
                id
                display
                active
                createdBy {
                    id
                    name
                }
                created
                updated
            }
            }
        }
      company
      templateIds
      projectDataCategoryIds
  }}`;
	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: 'POST',
		data: { query: graphqlQuery },
	});
	return response?.data?.updateProjectConfiguration;
}

export async function fetchProjectConfiguration(
	id: string
): Promise<ProjectConfiguration> {
	const graphqlQuery = `{
    projectConfiguration(id: "${id}") {
      id
      created
      updated
      name
      nameId
      description
      folders {
            id
            display
            active
            createdBy {
                id
                name
            }
            created
            updated
            subFolders {
                id
                display
                active
                createdBy {
                    id
                    name
                }
                created
                updated
                subFolders {
                id
                display
                active
                createdBy {
                    id
                    name
                }
                created
                updated
            }
            }
        }
      company
      templateIds
      projectDataCategoryIds
    }
  }`;
	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: 'POST',
		data: { query: graphqlQuery },
	});
	return response?.data?.projectConfiguration;
}

export async function addProjectConfigurationTemplate(
	configurationId: string,
	templateId: string
): Promise<ProjectConfiguration> {
	const graphqlQuery = `mutation { addProjectConfigurationTemplate(
    configurationId: "${configurationId}",
    templateId: "${templateId}"
  ) {
    id
    created
    updated
    name
    nameId
    description
    folders {
      id
    }
    company
    templateIds
  }}`;
	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: 'POST',
		data: { query: graphqlQuery },
	});
	return response?.data?.addProjectConfigurationTemplate;
}

export async function removeProjectConfigurationTemplate(
	configurationId: string,
	templateId: string
): Promise<ProjectConfiguration> {
	const graphqlQuery = `mutation { removeProjectConfigurationTemplate(
    configurationId: "${configurationId}",
    templateId: "${templateId}"
  ) {
    id
    created
    updated
    name
    nameId
    description
    folders {
      id
    }
    company
    templateIds
  }}`;
	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: 'POST',
		data: { query: graphqlQuery },
	});
	return response?.data?.removeProjectConfigurationTemplate;
}

export async function createProjectConfigurationFolder(
	configurationId: string,
	data: {
		display: string;
		active: boolean;
		// order: number;
		parentFolderId?: string;
	}
): Promise<ProjectConfiguration> {
	const graphqlQuery = `mutation { createProjectConfigurationFolder(
    configurationId: "${configurationId}",
    data: ${stringifyReqObj(data)}
  ) {
    id
    created
    updated
    name
    nameId
    description
    folders {
      id
      display
      active
      createdBy {
        id
        name
      }
      created
      updated
      subFolders {
        id
        display
        active
        createdBy {
          id
          name
        }
        created
        updated
      }
    }
    company
    templateIds
  }}`;
	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: 'POST',
		data: { query: graphqlQuery },
	});
	return response?.data?.createProjectConfigurationFolder;
}

export async function fetchProjectSubTypes(): Promise<ProjectSubType[]> {
	const graphqlQuery = `{
    projectSubTypes {
      id
      display
      options {
        id
        display
        rules {
          id
          created
          updated
          projectSubType
          projectSubTypeOption
          ruleType
          templates {
            id
            created
            updated
            name
            mimeType
          }
        }
      }
    }
  }`;
	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: 'POST',
		data: { query: graphqlQuery },
	});
	return response?.data?.projectSubTypes;
}

export async function updateProjectConfigurationFolder(
	configurationId: string,
	data: {
		id: string;
		display: string;
		active: boolean;
		parentFolderId?: string;
		folderPath?: string[];
	}
): Promise<ProjectConfiguration> {
	const graphqlQuery = `mutation { updateProjectConfigurationFolder(
    configurationId: "${configurationId}",
    data: ${stringifyReqObj(data)}
  ) {
    id
    created
    updated
    name
    nameId
    description
    folders {
      id
      display
      active
      createdBy {
        id
        name
      }
      created
      updated
      subFolders {
        id
        display
        active
        createdBy {
          id
          name
        }
        created
        updated
        subFolders {
          id
          display
          active
          createdBy {
            id
            name
          }
          created
          updated
        }
      }
    }
    company
    templateIds
    projectDataCategoryIds
  }}`;
	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: 'POST',
		data: { query: graphqlQuery },
	});
	return response?.data?.updateProjectConfigurationFolder;
}
